/**
 * cl-core/title — title 공식 + 순위 보호(rank_lock) (지시서 §1-5). 순수 TS.
 *
 * 형식 규칙
 *  - 구분자는 반각 세로선 하나(「 | 」).
 *  - 40자 안팎. 단지명·수요어는 앞 25자 안. 넘치면 동 → 세대수 → 구역명 순으로 뺀다.
 *  - 꼬리 「| 카더라」는 layout 템플릿이 붙인다 → 여기서 붙이지 않는다.
 *  - 「분양정보」·「전국」 접두어 금지.
 *
 * 순위 보호
 *  - (A) keyword_rank 7일 rank≤100 착지: 기존 title 그대로(연·월 토큰만 갱신). 스냅샷 원문이 있으면 absolute.
 *  - (B) 30일 네이버 유입 상위 200: 새 공식 적용하되 첫 단어 유지 · 기존 키워드 단어 하나라도 잃으면 기존 유지.
 *  - 기존 title 생성기는 지우지 않는다 — 호출부가 `cl.title_v2` 가 꺼져 있으면 기존 생성기 출력을 그대로 쓴다.
 *  - rank_lock 목록은 «입력» 이다(레인 0 의 docs/cl/rank_lock.json). 여기서 파일·DB 를 읽지 않는다.
 */
import { eokShortNum } from './money';
import { charLen, dedupeTypeWords, sidoShortName } from './names';
import { titleStageGroupOf } from './stages';

export const TITLE_SEP = ' | ';
export const TITLE_MAX = 40;
export const TITLE_HEAD_MAX = 25;
/** seo-detail (2026-10-25) — 상세 title «몸통» 상한. layout 이 「 | 카더라」(6자)를 붙이므로 전체 40자 안 = 몸통 34자. */
export const DETAIL_BODY_MAX = TITLE_MAX - 6;

/* ───────────────────────── 공통 ───────────────────────── */

function clean(s: string | null | undefined): string {
  return String(s ?? '').replace(/\s+/g, ' ').trim();
}

function joinSp(parts: Array<string | null | undefined>): string {
  return parts.map(clean).filter(Boolean).join(' ');
}

/** 「분양정보」·「전국」 접두어 제거 + 공백 정리. */
export function stripBannedPrefix(title: string): string {
  let s = clean(title);
  for (let i = 0; i < 3; i++) {
    const next = s.replace(/^(전국|분양정보)\s*[|:·\-—]?\s*/, '');
    if (next === s) break;
    s = next;
  }
  return s;
}

/** 만원 → 「4.1」(억 단위 숫자, 소수 1자리·끝 0 제거). */
export function eokNumber(manwon: number): string {
  return eokShortNum(manwon) ?? '0'; // 정본 lib/fmt(버림 — 하한이 올라가지 않게)
}

/**
 * site-audit 2026-10-24 text:zero_eok — 「분양가는 0억부터입니다」. 원천 supply_price_info.price_max 에 세대수(16·13)·면적(84·841)이
 *   금액 자리로 들어온 공고가 apt_sites.price_min 으로 옮겨졌고, 억 단위 버림이 「0억」을 냈다. 0.1억(1,000만원) 미만은 분양가로 볼 수 없는
 *   값이라 «없는 값» 으로 다룬다(그 칸·문장 생략 — 0억을 보이지 않는다). 원천 쪽은 sub-hmn-fill noticePrice 가 같은 문턱으로 거른다.
 */
export const MIN_PLAUSIBLE_PRICE_MANWON = 1000;
export function plausiblePrice(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= MIN_PLAUSIBLE_PRICE_MANWON;
}

/** 분양가 「4.1억~」(바닥부터 · 2026-10-24). 같은 값이면 「4.1억」. 값이 없으면 null. */
export function eokRange(minManwon?: number | null, maxManwon?: number | null): string | null {
  const a = plausiblePrice(minManwon) ? minManwon : null;
  const b = plausiblePrice(maxManwon) ? maxManwon : null;
  if (a == null && b == null) return null;
  // 2026-10-24 Node — 분양가는 «바닥부터»(「4.5억~」). 상한은 제목에 싣지 않는다(본문 표가 보조로 낸다).
  if (a != null && b != null && eokNumber(a) !== eokNumber(b)) return `${eokNumber(Math.min(a, b))}억~`;
  return `${eokNumber((a ?? b) as number)}억`;
}

function unitsWord(n?: number | null): string | null {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? `${Math.round(n)}세대` : null;
}

/* ───────────────────────── 상세 ───────────────────────── */

/**
 * seo_meta1 지시 2 — 꼬리는 lifecycle 단계 군(cl-core/stages titleStageGroupOf · 스테퍼 색인 단일 출처)이 고른다.
 *   'sale' 분양예정·청약 「분양정보·청약일정」 · 'building' 공사 중·입주 전 「분양가·입주예정」 · 'move_in' 입주 후 「입주·시세」 · 'redev' 정비 「진행현황·사업개요」.
 *   이벤트 덮어쓰기 둘은 그대로: 'remaining'(unsold_active 단계 하나가 근거 — 잔여세대) · 'model_house'(견본주택 D±14 · model_house_open).
 */
export type DetailTitleKind = 'sale' | 'building' | 'model_house' | 'remaining' | 'redev' | 'move_in';

export interface DetailTitleInput {
  /** 단지명(표시명). 구역명 괄호는 빼고 넘긴다 — 괄호가 있으면 여기서 떼어 구역명으로 쓴다. */
  name: string;
  /** 정비 구역명(예: 가야1구역). */
  districtName?: string | null;
  sigungu?: string | null;
  dong?: string | null;
  totalUnits?: number | null;
  lifecycleStage?: string | null;
  siteType?: string | null;
  /** 정비 원장 project_type(재개발/재건축 판별). */
  projectType?: string | null;
  /** 견본주택 개관일 YYYY-MM-DD(또는 YYYYMMDD). */
  modelHouseDate?: string | null;
  /** 오늘(KST) YYYY-MM-DD. 견본 D-14~D+14 판정. */
  today?: string | null;
  priceMinManwon?: number | null;
  priceMaxManwon?: number | null;
  /** 합성가면 가격을 제목에 쓰지 않는다. */
  priceSynthetic?: boolean;
  /** naver-rank R3 — 가격 원천 확인(cl-core isPriceSourced). false 면 「X억부터」를 싣지 않는다. 미지정이면 priceSynthetic 만 본다. */
  priceSourced?: boolean;
  /** naver-rank R3 — 연결 공고 국면(cl-core subscriptionPhase). 'rcept_open' 이면 접수 군. */
  subPhase?: 'announced' | 'rcept_open' | null;
  /** naver-rank R2 — 확인된 브랜드(브랜드 판정·표시명 브랜드)만. 추정 이름은 넘기지 않는다. */
  brand?: string | null;
  /** naver-rank R2 — 시공사(표시 정리된 값). */
  builder?: string | null;
  /** seo-detail — 견본주택(모델하우스) 개관일·위치 데이터가 있는가. 분양 꼬리 수요어 「모델하우스」(실측 월 97) 판정. */
  hasModelHouse?: boolean;
  /** seo-m 13(seo.detail_title_kw_v2) — 두 번째 자리 검색어(현장 검색량 순 · kw-map detailTitleKwWords). 비면 종전 단계 문형. rank_lock 경로는 호출부가 넘기지 않는다. */
  kwWords?: readonly string[] | null;
  /** name-lead (2026-11-02) — 정비 국면에서 브랜드 검색량이 구역명을 넘은 현장(apt_sites.name_lead.lead='brand'): 「{브랜드}({구역} {유형})」. */
  leadBrand?: boolean;
  /** name-lead — 분양·공사 국면에서 구역명 검색량이 단지명을 넘은 현장: 이름 뒤 「({구역})」. 넘치면 붙이지 않는다. */
  leadZone?: string | null;
}


function toDay(s: string | null | undefined): number | null {
  const t = clean(s).replace(/\./g, '-');
  const m = /^(\d{4})-?(\d{2})-?(\d{2})/.exec(t);
  if (!m) return null;
  const ms = Date.UTC(+m[1], +m[2] - 1, +m[3]);
  return Number.isFinite(ms) ? Math.round(ms / 86400000) : null;
}

/** 「더 다이너스티 가야(가야1구역)」 → { name: 더 다이너스티 가야, district: 가야1구역 } */
export function splitDistrict(name: string): { name: string; district: string | null } {
  const m = /^(.*?)\s*[(（]([^()（）]+)[)）]\s*$/.exec(clean(name));
  if (m && m[1].trim()) return { name: m[1].trim(), district: m[2].trim() };
  return { name: clean(name), district: null };
}

export function detailTitleKind(i: DetailTitleInput): DetailTitleKind {
  const st = i.lifecycleStage ?? '';
  const group = titleStageGroupOf(st, i.siteType);
  if (group === 'redev') return 'redev';
  if (st === 'unsold_active') return 'remaining';
  const mh = toDay(i.modelHouseDate);
  const td = toDay(i.today);
  if (mh != null && td != null && Math.abs(mh - td) <= 14 && (st === 'pre_announcement' || st === 'site_planning' || st === 'model_house_open' || st === '')) {
    return 'model_house';
  }
  if (st === 'model_house_open') return 'model_house';
  if (group === 'building') return 'building';
  if (group === 'moved_in') return 'move_in';
  return 'sale';
}

interface TitleParts {
  head: string;
  /** 꼬리 조각(빼는 순서 판정용 키 포함) */
  tail: Array<{ key: 'sigungu' | 'dong' | 'units' | 'fixed'; text: string }>;
  district: string | null;
}

function render(p: TitleParts, drop: Set<string>): string {
  const head = dedupeTypeWords(p.district && !drop.has('district') ? p.head.replace('{D}', `(${p.district})`) : p.head.replace('{D}', ''));
  const tail = p.tail.filter((t) => !drop.has(t.key)).map((t) => t.text).filter(Boolean).join(' ');
  return clean(tail ? `${clean(head)}${TITLE_SEP}${tail}` : clean(head));
}

/** 길이 맞춤: 동 → 세대수 → 구역명 순으로 뺀다. 그래도 넘치면 그대로(이름을 자르지 않는다). */
function fit(p: TitleParts, max = DETAIL_BODY_MAX): string {
  const order = ['dong', 'units', 'district'];
  const drop = new Set<string>();
  let t = render(p, drop);
  for (const k of order) {
    if (charLen(t) <= max) break;
    drop.add(k);
    t = render(p, drop);
  }
  // seo-detail — 그래도 넘치면 시군구까지(그걸로 상한 안에 들 때만 · 이름이 길어 어차피 넘치면 시군구는 남긴다).
  if (charLen(t) > max) {
    const t2 = render(p, new Set([...drop, 'sigungu']));
    if (charLen(t2) <= max) return t2;
  }
  return t;
}

/* ───────── naver-rank R3 — 분양 단계 군 · 가격 「X억부터」 ───────── */

export type SaleTitlePhase = 'pre' | 'receipt';

/** 분양 상세 title 의 단계 군. 청약 접수·당첨 발표·계약 단계이거나 연결 공고가 접수 중이면 'receipt', 그 밖은 'pre'(공고 전·접수 전). */
export function saleTitlePhase(i: Pick<DetailTitleInput, 'lifecycleStage' | 'subPhase'>): SaleTitlePhase {
  const st = String(i.lifecycleStage ?? '');
  if (i.subPhase === 'rcept_open') return 'receipt';
  if (['special_supply', 'subscription_open', 'award_pending', 'award_announced', 'contract_signing', 'contract'].includes(st)) return 'receipt';
  return 'pre';
}

/** 분양가 「4.1억부터」(바닥 = 최저가 · lib/fmt 버림). 값이 없거나 원천이 없으면 null — 데이터 없이 숫자를 싣지 않는다. */
export function eokFrom(minManwon?: number | null, maxManwon?: number | null): string | null {
  const vals = [minManwon, maxManwon].filter(plausiblePrice);
  if (!vals.length) return null;
  return `${eokNumber(Math.min(...vals))}억부터`;
}

function priceFromOf(i: DetailTitleInput): string | null {
  if (i.priceSynthetic || i.priceSourced === false) return null;
  return eokFrom(i.priceMinManwon, i.priceMaxManwon);
}

/* ───────── naver-rank R2 — 정비 title v3 ───────── */

const REDEV_TYPE_TAIL_RE = /\s*(?:주택)?(?:재개발|재건축)(?:\s*정비사업)?\s*$/;

/**
 * 정비 «구역명» 라벨: 앞머리 시도·시군구 토큰과 끝 유형어를 떼고, 번호로 끝나면 「구역」을 붙인다.
 *   「부산 동래구 사직2 재개발」→「사직2구역」 · 「시민공원주변재정비촉진1구역 재개발」→ 그대로 «…1구역» · 「반송동 재건축」→「반송동」.
 */
export function redevZoneLabel(raw: string | null | undefined, sigungu?: string | null): string {
  const src = clean(raw);
  if (!src) return '';
  const toks = src.split(' ');
  const sg = clean(sigungu);
  while (toks.length > 1) {
    const t = toks[0];
    const isPlace = !!sidoShortName(t) || (sg && t === sg) || (/^[가-힣]{1,6}(시|군|구)$/.test(t) && !/(구역|지구)$/.test(t));
    if (!isPlace) break;
    toks.shift();
  }
  let z = toks.join(' ').replace(REDEV_TYPE_TAIL_RE, '').trim();
  if (!z) return src;
  if (/[0-9A-Za-zⅠ-Ⅹ]$/.test(z)) z = `${z}구역`;
  return z;
}

/** 정비 유형어 — 원장 project_type 우선(재개발·재건축 둘만), 없으면 이름에서. */
export function redevTitleTypeWord(projectType?: string | null, ...names: Array<string | null | undefined>): '재개발' | '재건축' {
  const pt = clean(projectType);
  if (/재건축/.test(pt)) return '재건축';
  if (/재개발/.test(pt)) return '재개발';
  return /재건축/.test(names.map(clean).join(' ')) ? '재건축' : '재개발';
}

function cleanBuilder(b: string | null | undefined): string {
  return clean(b).replace(/\(주\)|㈜|주식회사/g, '').trim();
}

/** 정비 h1·title 머리: 「사직2구역 재개발」 · 브랜드 확인 시 「명륜2구역 재건축(래미안 마크 더 스위트)」. */
export function buildRedevHeading(i: Pick<DetailTitleInput, 'name' | 'districtName' | 'sigungu' | 'projectType' | 'brand' | 'leadBrand'>): string {
  const sp = splitDistrict(i.name);
  const zoneRaw = clean(i.districtName) || sp.district || sp.name;
  const zone = redevZoneLabel(zoneRaw, i.sigungu);
  const word = redevTitleTypeWord(i.projectType, i.name, i.districtName);
  // 구역명 안에 이미 유형어가 있으면(「도시정비형 재개발 정비구역」) 다시 붙이지 않는다.
  const core = /재개발|재건축/.test(zone) ? zone : `${zone} ${word}`;
  const brand = clean(i.brand);
  const showBrand = brand && squash(brand) !== squash(zone) && squash(core).indexOf(squash(brand)) < 0 && !/재개발|재건축|구역/.test(brand);
  if (!showBrand) return core;
  // name-lead — 브랜드가 더 많이 검색되는 현장은 브랜드를 앞에(구역·유형은 괄호로 남겨 구역 검색도 잇는다).
  return i.leadBrand ? `${brand}(${core})` : `${core}(${brand})`;
}

/** 정비 상세 title v3: 「{구역}구역 {재개발|재건축}({브랜드}) 진행현황 | {구} {N}세대 {시공사}」. 빈 조각은 깔끔히 뺀다. */
export function buildRedevTitleV3(i: DetailTitleInput): string {
  const max = DETAIL_BODY_MAX;
  const withBrand = buildRedevHeading(i);
  const noBrand = buildRedevHeading({ ...i, brand: null, leadBrand: false });
  const tailParts = { sg: clean(i.sigungu), units: unitsWord(i.totalUnits) ?? '', builder: cleanBuilder(i.builder) };
  const make = (head: string, drop: Set<string>) => {
    const tail = [tailParts.sg, drop.has('units') ? '' : tailParts.units, drop.has('builder') ? '' : tailParts.builder].filter(Boolean).join(' ');
    return clean(tail ? `${head} 진행현황${TITLE_SEP}${tail}` : `${head} 진행현황`);
  };
  // 넘치면 시공사 → 세대수 → 브랜드 순으로 뺀다(구역명·유형·구는 남긴다).
  const steps: Array<[string, string[]]> = [[withBrand, []], [withBrand, ['builder']], [withBrand, ['builder', 'units']]];
  /* name-lead — 브랜드가 대표 이름인 현장은 넘쳐도 브랜드를 버리지 않는다: 「{브랜드} {유형}」(구역은 h1 괄호에 남는다) → 그래도 넘치면 종전처럼 구역만. */
  const brandClean = clean(i.brand);
  if (i.leadBrand && brandClean && withBrand !== noBrand) {
    const word = redevTitleTypeWord(i.projectType, i.name, i.districtName);
    steps.push([`${brandClean} ${word}`, ['builder', 'units']]);
  }
  steps.push([noBrand, ['builder', 'units']]);
  let t = make(withBrand, new Set());
  for (const [h, d] of steps) {
    t = make(h, new Set(d));
    if (charLen(t) <= max) break;
  }
  return stripBannedPrefix(t);
}

/** naver-rank R6 — 지역 정비 허브 카드 앵커 = 대상 상세 h1 과 같은 정식명(「사직2구역 재개발」 · 「자이 엘리스트(사직3구역)」→「사직3구역 재개발(자이 엘리스트)」). */
export function redevAnchorName(name: string, projectType?: string | null, sigungu?: string | null, lead?: { lead: 'brand' | 'zone'; brand: string | null } | null): string {
  const sp = splitDistrict(name);
  // name-lead — 확정 대표 이름이 브랜드면(상세 h1 과 같게) 브랜드를 앞에. 확정값의 브랜드는 원장에 쓰인 이름만(크론 판정).
  const brand = sp.district && /구역$/.test(sp.district) ? sp.name : lead?.brand ?? null;
  const h = buildRedevHeading({ name, districtName: sp.district, sigungu, projectType, brand, leadBrand: lead?.lead === 'brand' });
  return h || clean(name);
}

/**
 * seo-m 13 — 검색량 순 두 번째 검색어로 head 를 짠다: 「{이름} 분양가[ X억부터]·{w1}[·{w2}]」.
 *   견본 국면(model_house)은 「모델하우스」를 앞에 둔다. 두 낱말이 들어가도 시군구가 남고 상한 안이면 두 낱말, 아니면 한 낱말.
 *   한 낱말로도 시군구가 빠지면 null(종전 문형) — 검색어 하나 더 넣자고 지역 낱말을 잃지 않는다.
 */
function kwTitle(name: string, kind: DetailTitleKind, words: readonly string[], loc: TitleParts['tail'], i: DetailTitleInput): string | null {
  const from = kind === 'building' ? null : priceFromOf(i);
  let ws = [...words];
  if (kind === 'model_house') ws = ['모델하우스', ...ws.filter((w) => w !== '모델하우스' && w !== '견본주택')];
  const sg = clean(i.sigungu);
  for (const n of [Math.min(2, ws.length), 1]) {
    if (n < 1) continue;
    const word = ws.slice(0, n).join('·');
    const head = from ? `${name} 분양가 ${from}·${word}` : `${name} 분양가·${word}`;
    const t = fit({ head, tail: loc, district: null });
    if (charLen(t) <= DETAIL_BODY_MAX && (!sg || t.includes(sg))) return t;
  }
  return null;
}

/** 상세 title(v2). 꼬리 「| 카더라」 없음. */
export function buildDetailTitle(i: DetailTitleInput): string {
  const lz = clean(i.leadZone);
  if (lz && detailTitleKind(i) !== 'redev') {
    // name-lead — 구역명이 더 많이 검색되는 분양·공사 현장: 「{단지명}({구역})」로 한 번 짜 보고, 상한을 넘거나 시군구가 빠지면 종전 이름만.
    const sp0 = splitDistrict(i.name);
    if (squash(sp0.name).indexOf(squash(lz)) < 0) {
      const t = buildDetailTitle({ ...i, leadZone: null, nameExact: `${sp0.name}(${lz})` } as DetailTitleInput);
      // 시군구 낱말은 남아야 한다 — 「김포시」가 빠져도 「김포북변」처럼 어간이 이름에 남으면 지역 신호는 그대로다.
      const sg = clean(i.sigungu).split(' ').pop() ?? '';
      const sgStem = sg.replace(/(시|군|구)$/, '');
      if (charLen(t) <= DETAIL_BODY_MAX && t.includes(lz) && (!sg || t.includes(sg) || (sgStem.length >= 2 && t.includes(sgStem)))) return t;
    }
    return buildDetailTitle({ ...i, leadZone: null });
  }
  const sp = splitDistrict(i.name);
  // name-lead 내부 — 괄호까지 이름으로 쓰는 한 번(splitDistrict 가 괄호를 떼지 않게).
  const name = (i as DetailTitleInput & { nameExact?: string }).nameExact ?? sp.name;
  const kind = detailTitleKind(i);
  const loc = [
    { key: 'sigungu' as const, text: clean(i.sigungu) },
    { key: 'dong' as const, text: clean(i.dong) },
    { key: 'units' as const, text: unitsWord(i.totalUnits) ?? '' },
  ];
  const kw = (i.kwWords ?? []).map((w) => clean(w)).filter(Boolean);
  if (kw.length && (kind === 'sale' || kind === 'model_house' || kind === 'building')) {
    const t = kwTitle(name, kind, kw, loc, i);
    if (t) return stripBannedPrefix(t);
  }
  let parts: TitleParts;
  switch (kind) {
    case 'model_house':
      parts = { head: `${name} 분양가·모델하우스`, tail: loc, district: null };
      break;
    case 'building':
      parts = { head: `${name} 분양가·입주예정`, tail: loc, district: null };
      break;
    case 'remaining': {
      // naver-rank R3 — 잔여·선착순 국면: 「분양가 X억부터·선착순」(가격 원천 있을 때만 숫자).
      const from = priceFromOf(i);
      parts = { head: from ? `${name} 분양가 ${from}·선착순` : `${name} 분양가·선착순`, tail: [loc[0]], district: null };
      break;
    }
    case 'redev':
      // naver-rank R2 — 정비 title v3(형제 구역과 갈리는 재료: 구역명·유형·브랜드·세대·시공사). 구 반복 없음.
      return buildRedevTitleV3(i);
    case 'move_in':
      parts = { head: `${name} 입주·시세`, tail: [loc[0], loc[1]], district: null };
      break;
    default: {
      // naver-rank R3 — 단계별 수요어. 공고 전 「분양가·청약일정」 · 접수·발표 대기 「분양가·경쟁률·당첨발표」.
      const from = priceFromOf(i);
      // seo-detail — 수요 순위(분양가 392 > 모델하우스 97 > 청약 67): 견본주택 데이터가 있으면 공고 전 꼬리는 「모델하우스」.
      const word = saleTitlePhase(i) === 'receipt' ? '경쟁률·당첨발표' : i.hasModelHouse ? '모델하우스' : '청약일정';
      parts = { head: from ? `${name} 분양가 ${from}·${word}` : `${name} 분양가·${word}`, tail: loc, district: null };
    }
  }
  return stripBannedPrefix(fit(parts));
}

/* ───────────────────────── 지역 ───────────────────────── */

export type RegionTitleTab = 'main' | 'subscription' | 'unsold' | 'redev';

export interface RegionTitleInput {
  sido: string;
  sigungu?: string | null;
  /** 연·월 — 호출부가 기준일(KST)에서 넘긴다. */
  year: number;
  month: number;
}

export function buildRegionTitle(tab: RegionTitleTab, i: RegionTitleInput): string {
  const place = joinSp([i.sido, i.sigungu]);
  const ym = `${i.year}년 ${i.month}월`;
  switch (tab) {
    case 'subscription':
      // seo-detail — 실측 수요형 「{지역} 청약」(부산 3,030·울산 1,880)을 앞에. KWX 레인 C — 「{지역} 청약 일정 — 접수·발표일」(지역별 청약일정 착지).
      return stripBannedPrefix(`${place} 청약 일정 — 접수·발표일${TITLE_SEP}분양·무순위`);
    case 'unsold':
      return stripBannedPrefix(`${clean(i.sigungu) || clean(i.sido)} 미분양 아파트${TITLE_SEP}줍줍·무순위·선착순 (${ym})`);
    case 'redev':
      return stripBannedPrefix(`${place} 재개발·재건축 현황${TITLE_SEP}${i.year}년`);
    default:
      // seo-detail — 수요형 「{지역} 청약」·「{지역} 아파트 분양」·「{지역} 분양 일정」을 한 머리에(꼬리 포함 40자 안).
      return stripBannedPrefix(`${place} 청약·아파트 분양 일정${TITLE_SEP}${ym}`);
  }
}

/* ───────────────────────── 카페 ───────────────────────── */

export interface CafeSiteTitleInput {
  name: string;
  districtName?: string | null;
  /** 이벤트·수요어(예: 「모델하우스 오픈」「분양가 공개」) */
  eventWord: string;
  sigungu?: string | null;
  units?: number | null;
}

/** 카페 단지 글: 「{단지명}({구역명}) {이벤트·수요어} — {시군구} {N}세대」 */
export function buildCafeSiteTitle(i: CafeSiteTitleInput): string {
  const sp = splitDistrict(i.name);
  const d = clean(i.districtName) || sp.district;
  const head = dedupeTypeWords(`${sp.name}${d ? `(${d})` : ''} ${clean(i.eventWord)}`);
  const tail = joinSp([i.sigungu, unitsWord(i.units)]);
  return stripBannedPrefix(tail ? `${clean(head)} — ${tail}` : clean(head));
}

export interface CafeSeriesTitleInput {
  region: string;
  name: string;
  stageOrEvent: string;
  units?: number | null;
}

/** 카페 지역 시리즈: 「{지역} {단지·구역명} {단계·이벤트} {N세대}」 */
export function buildCafeSeriesTitle(i: CafeSeriesTitleInput): string {
  return stripBannedPrefix(dedupeTypeWords(joinSp([i.region, i.name, i.stageOrEvent, unitsWord(i.units)])));
}

/* ───────────────────────── 형식 검사 ───────────────────────── */

export interface TitleCheck {
  ok: boolean;
  problems: string[];
}

/** 형식 검사(게이트·content-gates 공용). `head` = 단지명/지역명(앞 25자 안 확인). */
export function checkTitleFormat(title: string, head?: string | null): TitleCheck {
  const t = clean(title);
  const problems: string[] = [];
  if (/[｜]/.test(t)) problems.push('전각 세로선');
  if ((t.match(/\|/g) || []).length > 1) problems.push('세로선 2개 이상');
  if (/카더라\s*$/.test(t)) problems.push('꼬리 카더라 직접 부착');
  if (/^(전국|분양정보)/.test(t)) problems.push('금지 접두어');
  if (charLen(t) > TITLE_MAX + 5) problems.push(`길이 ${charLen(t)}자`);
  if (head) {
    const at = t.indexOf(clean(head));
    if (at < 0 || charLen(t.slice(0, at + clean(head).length)) > TITLE_HEAD_MAX) problems.push('이름이 앞 25자 밖');
  }
  return { ok: problems.length === 0, problems };
}

/* ───────────────────────── title_v2 플래그 ───────────────────────── */

export interface TitleV2Flags {
  detail: boolean;
  region: boolean;
  cafe: boolean;
}

/** 키가 없으면 전부 off — 배포 후 post_deploy 에서 켠다(§0-2). */
export const TITLE_V2_DEFAULT: TitleV2Flags = { detail: false, region: false, cafe: false };

/** app_config(namespace 'cl', key 'title_v2') 값 파싱. 형식이 틀리면 off. */
export function parseTitleV2(value: unknown): TitleV2Flags {
  if (value === true) return { detail: true, region: true, cafe: true };
  if (!value || typeof value !== 'object') return { ...TITLE_V2_DEFAULT };
  const v = value as Record<string, unknown>;
  return { detail: v.detail === true, region: v.region === true, cafe: v.cafe === true };
}

/* ───────────────────────── rank_lock ───────────────────────── */

export type RankLockSet = 'A' | 'B' | 'AB';

export interface RankLockEntry {
  /** 착지 URL 또는 경로. 한글 slug 는 인코딩 여부와 무관하게 대조한다. */
  url: string;
  set: RankLockSet;
  /** T0 스냅샷 title 원문(꼬리 포함 가능). */
  title?: string | null;
  /** (A) 순위 키워드들. */
  keywords?: string[] | null;
  h1?: string | null;
}

export interface RankLockFile {
  version?: number;
  generated_at?: string;
  entries: RankLockEntry[];
}

function safeDecode(s: string): string {
  let out = s;
  for (let i = 0; i < 3; i++) {
    try {
      const d = decodeURIComponent(out);
      if (d === out) break;
      out = d;
    } catch {
      break;
    }
  }
  return out;
}

/** URL·경로 → 대조 키(호스트·쿼리·해시·끝 슬래시 제거, 퍼센트 디코드, NFC). */
export function normalizeLockPath(url: string): string {
  let s = clean(url);
  s = s.replace(/^https?:\/\/[^/]+/i, '');
  s = s.split('#')[0].split('?')[0];
  s = safeDecode(s);
  if (typeof s.normalize === 'function') s = s.normalize('NFC');
  if (!s.startsWith('/')) s = `/${s}`;
  if (s.length > 1) s = s.replace(/\/+$/, '');
  return s;
}

/** 경로 → 항목 색인. 같은 경로가 A·B 둘 다면 'AB' 로 합친다. */
export function indexRankLock(lock: RankLockFile | RankLockEntry[] | null | undefined): Map<string, RankLockEntry> {
  const entries = Array.isArray(lock) ? lock : lock?.entries ?? [];
  const map = new Map<string, RankLockEntry>();
  for (const e of entries) {
    if (!e || !e.url) continue;
    const k = normalizeLockPath(e.url);
    const prev = map.get(k);
    if (!prev) {
      map.set(k, { ...e, keywords: e.keywords ? [...e.keywords] : e.keywords });
      continue;
    }
    const set: RankLockSet = prev.set === e.set ? prev.set : 'AB';
    const kw = Array.from(new Set([...(prev.keywords ?? []), ...(e.keywords ?? [])]));
    map.set(k, { ...prev, set, title: prev.title || e.title, h1: prev.h1 || e.h1, keywords: kw });
  }
  return map;
}

export function findRankLock(lock: RankLockFile | RankLockEntry[] | Map<string, RankLockEntry> | null | undefined, url: string): RankLockEntry | null {
  const map = lock instanceof Map ? lock : indexRankLock(lock);
  return map.get(normalizeLockPath(url)) ?? null;
}

/* ───────── naver-rank R1 — rank_lock 자동 해제 ─────────
 * 규칙(2026-10-25 Node 위임 결정): 잠긴 URL 의 잠금 키워드 일 순위(그 URL 이 아니면 권외 101)가
 *   «최근 측정 7일 연속» 최고 순위보다 5 이상 나쁘거나 권외(>100)면 해제 → 새 공식. 단 최근 순위 1~10 은 잠금 유지.
 * 운영 판정은 DB 함수(cl_rank_lock_release_refresh · pg_cron 매일)가 하고 app_config cl/rank_lock_released 에 목록을 쓴다.
 * 이 함수는 같은 규칙의 순수 거울(테스트·점검용)이다. */

export const RANK_LOCK_RELEASE_WINDOW = 7;
export const RANK_LOCK_RELEASE_DROP = 5;
export const RANK_LOCK_KEEP_TOP = 10;
export const RANK_OUT = 101;

export interface RankDay { date: string; rank: number | null }

export interface LockReleaseDecision { release: boolean; reason: 'keep_top' | 'insufficient' | 'dropped' | 'out' | 'stable'; best: number | null; current: number | null }

export function decideLockRelease(days: ReadonlyArray<RankDay>): LockReleaseDecision {
  const norm = days
    .map((d) => ({ date: clean(d.date), r: typeof d.rank === 'number' && d.rank >= 1 && d.rank <= 100 ? d.rank : RANK_OUT }))
    .filter((d) => d.date)
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  if (!norm.length) return { release: false, reason: 'insufficient', best: null, current: null };
  const best = Math.min(...norm.map((d) => d.r));
  const current = norm[0].r;
  if (current <= RANK_LOCK_KEEP_TOP) return { release: false, reason: 'keep_top', best, current };
  const win = norm.slice(0, RANK_LOCK_RELEASE_WINDOW);
  if (win.length < RANK_LOCK_RELEASE_WINDOW) return { release: false, reason: 'insufficient', best, current };
  if (win.every((d) => d.r > 100)) return { release: true, reason: 'out', best, current };
  if (win.every((d) => d.r > 100 || d.r >= best + RANK_LOCK_RELEASE_DROP)) return { release: true, reason: 'dropped', best, current };
  return { release: false, reason: 'stable', best, current };
}

/** app_config cl/rank_lock_released 값({ urls: string[] } 또는 string[]) → 정규화 경로 집합. 형식이 틀리면 빈 집합(= 해제 없음 · 잠금 유지 쪽). */
export function parseRankLockReleased(value: unknown): Set<string> {
  const arr = Array.isArray(value) ? value : value && typeof value === 'object' && Array.isArray((value as { urls?: unknown }).urls) ? (value as { urls: unknown[] }).urls : [];
  const out = new Set<string>();
  for (const u of arr) if (typeof u === 'string' && clean(u)) out.add(normalizeLockPath(u));
  return out;
}

/** 해제 목록을 뺀 rank_lock(title·h1 판정 전용). 원본은 그대로 둔다 — 색인 예외(rankLocked) 같은 다른 용도는 원본을 쓴다. */
export function releaseRankLock(lock: RankLockFile | null | undefined, released: ReadonlySet<string> | null | undefined): RankLockFile | null {
  if (!lock) return null;
  if (!released || released.size === 0) return lock;
  return { ...lock, entries: (lock.entries ?? []).filter((e) => !e?.url || !released.has(normalizeLockPath(e.url))) };
}

/**
 * seo-m 13 — 상세 title 두 번째 검색어(kwWords)를 넣어도 되는가.
 *   판정은 «title 판정용 잠금»(releaseRankLock 을 거친 rankLockTitle) 하나로 한다 — title 잠금이 풀린 경로(예: 자동 해제 목록)는 v2 문형,
 *   아직 잠긴 경로는 넣지 않는다(title 바이트 불변). 색인 예외·킥·h1 외 보존 문장 등 다른 잠금 용도는 원본 rank_lock 을 그대로 쓴다.
 *   잠금 목록을 못 읽었으면(null) false — 보호 대상을 모른 채 title 을 바꾸지 않는다(CL12 실패 안전).
 */
export function detailTitleKwAllowed(lockTitle: RankLockFile | null | undefined, url: string): boolean {
  if (!lockTitle) return false;
  return !findRankLock(lockTitle, url);
}

/* ───────── seo-detail (2026-10-25) — 잠긴(옛) title 렌더 시점 정리 ─────────
 *
 * 네이버 실측: 상세 title 27%가 「카....」로 잘린다(꼬리 「 | 카더라」 포함 40자 초과). 잠긴 스냅샷·옛 생성기 title 에는
 *   금지어 「분양정보」, 같은 값 범위 「3.8억~3.8억」, 상한 범위 「9.2억~13.2억」(바닥 먼저 원칙 위반)이 남아 있다.
 *   스냅샷(app_config)은 고치지 않고 «렌더 시점» 에만 최소로 다듬는다 — 나머지 글자는 그대로 둔다(순위 보존).
 * 순서: ① 「분양정보」→「분양가」(이미 「분양가」가 있으면 그 조각을 뺀다) ② 같은 값 범위 → 한 값 · 범위 → 「X억부터」
 *       ③ 전체(꼬리 포함) 40자 초과면 가치 낮은 끝 조각부터: 「 — 지역」 → 「· 입주일정/청약일정」(실측 검색량 ≤11). 이름·가격은 자르지 않는다.
 */
export const BRAND_TAIL = ' | 카더라';
/** 꼬리(「 | 카더라」)를 포함한 전체 title 목표 길이. */
export const FULL_TITLE_MAX = TITLE_MAX;
const BRAND_TAIL_RE = /\s*\|\s*카더라\s*$/;
const NUM = '(\\d+(?:\\.\\d+)?)';
const EOK_RANGE_RE = new RegExp(`${NUM}억\\s*[~∼～-]\\s*${NUM}억`, 'g');

function fmtEokNum(s: string): string {
  const n = Number(s);
  return Number.isFinite(n) ? String(Math.round(n * 10) / 10) : s;
}

/** 「분양정보」 금지어 정리(한 문자열). */
export function replaceBannedInfoWord(s: string): string {
  let t = String(s ?? '');
  if (!/분양정보/.test(t)) return t;
  if (/분양가/.test(t)) {
    // 「분양가」가 이미 있으면 「분양정보」 조각과 이웃 구분자 하나를 뺀다.
    t = t.replace(/분양정보\s*[·,/]\s*/g, '').replace(/\s*[·,/]\s*분양정보/g, '').replace(/분양정보/g, '');
  } else {
    t = t.replace(/분양정보/g, '분양가');
  }
  return clean(t);
}

/** 범위 → 바닥 먼저: 「3.8억~3.8억」→「3.8억」 · 「9.2억~13.2억」→「9.2억부터」. */
export function floorFirstRange(s: string): string {
  return String(s ?? '').replace(EOK_RANGE_RE, (_m, a: string, b: string) => {
    const x = Number(a);
    const y = Number(b);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return _m;
    if (fmtEokNum(a) === fmtEokNum(b)) return `${fmtEokNum(a)}억`;
    return `${fmtEokNum(String(Math.min(x, y)))}억부터`;
  });
}

/**
 * 잠긴·옛 title 다듬기. `withBrandTail` = 입력에 「 | 카더라」가 붙어 있는가(스냅샷 absolute). 출력도 같은 꼴로 돌려준다.
 *   길이는 꼬리 포함 FULL_TITLE_MAX 를 목표로 «끝 조각만» 뺀다 — 목표에 못 미쳐도 이름·가격은 자르지 않는다.
 */
export function sanitizeLegacyTitle(title: string, max = FULL_TITLE_MAX): string {
  const raw = clean(title);
  if (!raw) return raw;
  const hadTail = BRAND_TAIL_RE.test(raw);
  let body = clean(raw.replace(BRAND_TAIL_RE, ''));
  body = clean(floorFirstRange(replaceBannedInfoWord(body)));
  const total = (b: string) => charLen(b) + charLen(BRAND_TAIL);
  // ③-a 끝의 「 — 지역」 조각(마지막 대시 뒤)부터.
  for (let i = 0; i < 2 && total(body) > max; i++) {
    const at = body.lastIndexOf(' — ');
    if (at <= 0) break;
    const head = body.slice(0, at).trim();
    if (charLen(head) < 4) break;
    body = head;
  }
  // ③-b 보조 수요어(검색량 최하위) — 「· 입주일정」 「· 청약일정」.
  for (const w of ['입주일정', '청약일정']) {
    if (total(body) <= max) break;
    const next = clean(body.replace(new RegExp(`\\s*·\\s*${w}`), ''));
    if (next !== body && charLen(next) >= 4) body = next;
  }
  return hadTail ? `${body}${BRAND_TAIL}` : body;
}

/** 연·월 토큰만 갱신(「2026년 9월」→「2026년 10월」, 「(2026.09 기준)」→「(2026.10 기준)」). */
export function refreshYearMonth(title: string, year: number, month: number): string {
  return String(title)
    .replace(/(20\d{2})년\s?(\d{1,2})월/g, `${year}년 ${month}월`)
    .replace(/(20\d{2})\.(\d{1,2})(?=\s*기준)/g, `${year}.${month < 10 ? `0${month}` : month}`);
}

const KEYWORD_STOP = new Set(['카더라', '총정리', '정리', '안내', '정보', '및', '최신']);

/** 기존 title 의 «키워드 단어». 꼬리 「카더라」·숫자뿐인 토큰(연도)은 뺀다. */
export function titleKeywords(title: string): string[] {
  const body = clean(title).replace(/\s*\|\s*카더라\s*$/, '');
  return Array.from(new Set(
    body.split(/[\s|·,/\-—–()（）[\]:]+/).map((w) => w.trim()).filter((w) => w && !/^\d+$/.test(w) && !KEYWORD_STOP.has(w) && charLen(w) >= 2),
  ));
}

/** 지역 낱말(시도 짧은 이름·「…시/군/구」) — 구역·지구 이름은 아니다. */
function isPlaceToken(w: string): boolean {
  const t = clean(w);
  if (!t) return false;
  if (sidoShortName(t)) return true;
  return /^[가-힣]{1,5}(시|군|구)$/.test(t) && !/(구역|지구)$/.test(t);
}

function firstWord(title: string): string {
  return clean(title).split(/[\s|·]+/)[0] ?? '';
}

/** seo-detail — B 판정의 «첫 단어»: 앞머리 지역 낱말(「부산 해운대구 …」)은 건너뛴다(정비 title 이 구역명으로 시작하게). */
function firstKeyWord(title: string): string {
  const ws = clean(title).split(/[\s|·]+/).filter(Boolean);
  return ws.find((w) => !isPlaceToken(w)) ?? ws[0] ?? '';
}

function squash(s: string): string {
  return s.replace(/\s+/g, '');
}

export type RankLockDecision = 'v2' | 'legacy' | 'locked_a' | 'kept_b' | 'v2_b';

export interface RankLockResult {
  title: string;
  /** true 면 호출부가 `title: { absolute }` 로 내보낸다(꼬리 이중 부착 방지). */
  absolute: boolean;
  decision: RankLockDecision;
}

export interface ApplyRankLockInput {
  url: string;
  /** 새 공식 결과(꼬리 없음). title_v2 가 꺼져 있으면 null. */
  candidate: string | null;
  /** 기존 생성기 결과(꼬리 없음). */
  legacy: string;
  lock?: RankLockFile | RankLockEntry[] | Map<string, RankLockEntry> | null;
  /** 연·월 갱신 기준(KST). 없으면 갱신하지 않는다. */
  year?: number;
  month?: number;
}

/**
 * KWX 레인 C — (B) 잠금 항목이 새 후보에 요구하는 «보존 키워드»(applyRankLock B 판정과 같은 집합 · 지역 낱말 제외).
 *   (A)·(AB) 잠금이면 null(후보를 쓰지 않는다) · 잠금 아님이면 []. 몸통 길이(꼬리 제외)도 같이 준다(그 길이보다 길어지지 않게 조립하라는 뜻).
 */
export function rankLockRequiredWords(lock: ApplyRankLockInput['lock'], url: string, legacy: string): { words: string[]; bodyLen: number } | null {
  const entry = lock ? findRankLock(lock as never, url) : null;
  if (!entry) return { words: [], bodyLen: 0 };
  if (entry.set === 'A' || entry.set === 'AB') return null;
  const base = clean(entry.title) || legacy;
  const words = Array.from(new Set([...titleKeywords(base), ...(entry.keywords ?? []).flatMap((k) => titleKeywords(k))])).filter((w) => !isPlaceToken(w));
  return { words, bodyLen: charLen(clean(base).replace(/\s*\|\s*카더라\s*$/, '')) };
}

/**
 * 최종 title 결정.
 *  - (A) 포함: 스냅샷 원문이 있으면 그것(absolute), 없으면 legacy. 연·월 토큰만 갱신.
 *  - (B): candidate 가 legacy 의 첫 단어로 시작하고 legacy 키워드(+항목 keywords)를 전부 품으면 candidate, 아니면 legacy.
 *  - 비대상: candidate ?? legacy.
 */
export function applyRankLock(i: ApplyRankLockInput): RankLockResult {
  const entry = i.lock ? findRankLock(i.lock as never, i.url) : null;
  const ym = (t: string) => (i.year && i.month ? refreshYearMonth(t, i.year, i.month) : t);
  if (entry && (entry.set === 'A' || entry.set === 'AB')) {
    const snap = clean(entry.title);
    if (snap) return { title: ym(snap), absolute: true, decision: 'locked_a' };
    return { title: ym(i.legacy), absolute: false, decision: 'locked_a' };
  }
  if (!i.candidate) return { title: i.legacy, absolute: false, decision: 'legacy' };
  if (entry && entry.set === 'B') {
    const base = clean(entry.title) || i.legacy;
    const fw = firstWord(base);
    const fkw = firstKeyWord(base);
    const cand = clean(i.candidate);
    // seo-detail — 첫 단어가 지역 낱말이면(「부산 해운대구 재송4 …」) 첫 «비지역» 단어로 시작해도 같은 머리로 본다(정비 title 이 구역명으로 시작).
    if (fw && !cand.startsWith(fw) && !(fkw !== fw && fkw && cand.startsWith(fkw))) return { title: i.legacy, absolute: false, decision: 'kept_b' };
    // seo-detail — 지역 낱말은 «보존 키워드» 에서 뺀다(새 공식은 구를 «|» 뒤로 보낸다 · 시도는 싣지 않는다).
    const need = Array.from(new Set([...titleKeywords(base), ...(entry.keywords ?? []).flatMap((k) => titleKeywords(k))])).filter((w) => !isPlaceToken(w));
    const sc = squash(cand);
    for (const w of need) {
      if (sc.indexOf(squash(w)) < 0) return { title: i.legacy, absolute: false, decision: 'kept_b' };
    }
    return { title: cand, absolute: false, decision: 'v2_b' };
  }
  return { title: clean(i.candidate), absolute: false, decision: 'v2' };
}

/* ───────────────────────── 본문 보존율 ───────────────────────── */

function sentences(text: string): string[] {
  return String(text ?? '')
    .replace(/\s+/g, ' ')
    .split(/(?:[.!?。]|다\.)\s+|\n+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * 키워드 토큰을 포함한 (기존) 문장의 보존율(§1-5 본문 A·B 공통 ≥95%).
 * 기존 문장 중 키워드를 하나라도 품은 것들이 새 본문에 공백 무시 부분일치로 남아 있는 비율. 대상 0이면 1.
 */
export function keywordSentenceRetention(oldText: string, newText: string, keywords: string[]): number {
  const kws = keywords.map(squash).filter(Boolean);
  if (kws.length === 0) return 1;
  const target = sentences(oldText).filter((s) => kws.some((k) => squash(s).indexOf(k) >= 0));
  if (target.length === 0) return 1;
  const hay = squash(newText);
  const kept = target.filter((s) => hay.indexOf(squash(s)) >= 0).length;
  return kept / target.length;
}
